// src/services/userApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

// User Types - Updated for Identity System
export interface User {
  userId: number;
  identityUserId?: string; // GUID from AspNetUsers (optional)
  email: string;
  displayName: string | null;
  roles: string[]; // Array of role names (Identity system)
  role: string | null; // First role (for backward compatibility)
  isActive: boolean;
  emailConfirmed: boolean; // Identity field
  lockoutEnabled: boolean; // Identity field
  lockoutEnd: string | null; // Identity field (ISO 8601 datetime or null)
  accessFailedCount: number; // Identity field
  lastLoginAt?: string | null;
  createdAt: string; // ISO 8601 datetime
  company?: string | null;
  userType?: string | null; // TENANT | FRAPORT | CLIENT
}

export interface UserSearchResponse {
  users: User[];
  total: number;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
}

// Error Type
export interface UserApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
}

// Search users. Sprint D2: excludeProjectId excludes users already in that project (for invite-to-project).
export async function searchUsers(
  query: string,
  limit?: number,
  excludeProjectId?: number | null
): Promise<UserSearchResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('q', query);
  if (limit) queryParams.append('limit', limit.toString());
  if (excludeProjectId != null && excludeProjectId > 0) {
    queryParams.append('excludeProjectId', excludeProjectId.toString());
  }

  const url = `${API_BASE_URL}/api/users/search?${queryParams.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to search users: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  return response.json();
}

// List users with pagination (Admin endpoint - uses Identity system)
export async function listUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
}): Promise<UsersListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

  // Updated to use /api/admin/users endpoint (Identity system)
  const url = `${API_BASE_URL}/api/admin/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to list users: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  const data = await response.json();

  // Handle different response formats:
  // 1. { users: [], total: number, page: number, pageSize: number } - Expected format
  // 2. [] - Array of users directly (fallback)
  // 3. { users: [] } - Just users array
  let usersArray: any[] = [];
  let totalCount = 0;
  let pageNum = params?.page || 1;
  let pageSizeNum = params?.pageSize || 25;
  
  if (Array.isArray(data)) {
    // Response is an array directly
    usersArray = data;
    totalCount = data.length;
  } else if (data.users && Array.isArray(data.users)) {
    // Response has users property
    usersArray = data.users;
    totalCount = data.total !== undefined ? data.total : data.users.length;
    pageNum = data.page !== undefined ? data.page : pageNum;
    pageSizeNum = data.pageSize !== undefined ? data.pageSize : pageSizeNum;
  } else {
    // Unexpected format
    console.warn('listUsers: Unexpected response format:', data);
    usersArray = [];
    totalCount = 0;
  }
  
  // Process users array - ensure roles array exists
  const processedUsers = usersArray.map((user: any) => {
    // Ensure roles array exists (backend should provide it, but handle missing case)
    if (!user.roles && user.role) {
      user.roles = [user.role];
    } else if (!user.roles) {
      user.roles = [];
    }
    // Ensure role field exists for backward compatibility (use first role)
    if (!user.role && user.roles && user.roles.length > 0) {
      user.role = user.roles[0];
    } else if (!user.role) {
      user.role = null;
    }
    return user;
  });
  
  const result = {
    users: processedUsers,
    total: totalCount,
    page: pageNum,
    pageSize: pageSizeNum,
  };

  return result;
}

// Admin user management functions

export interface UpdateUserRequest {
  email?: string;
  displayName?: string;
  company?: string | null;
  userType?: string | null; // TENANT | FRAPORT | CLIENT
  roleName?: string | null; // Backend expects 'roleName' not 'role'
}

export interface UpdateUserResponse {
  success: boolean;
  message: string;
  user: User; // User object with roles: string[]
  roleUpdated: boolean;
}

export interface InviteUserRequest {
  email: string;
  roleName: string; // Identity role name: "System Admin", "Airport Manager", etc.
  userAccessToken?: string | null; // Optional user access token for delegated authentication
}

export interface InviteUserResponse {
  invitationId: number;
  email: string;
  roleName: string;
  expiresAt: string; // ISO 8601 datetime
  invitationLink: string;
}

export interface ActivateUserResponse {
  success: boolean;
  message: string;
  userId: number;
  user?: User; // Optional: updated user with roles: string[]
}

export interface DeactivateUserResponse {
  success: boolean;
  message: string;
  userId: number;
  user?: User; // Optional: updated user with roles: string[]
}

/**
 * Update user (Admin only)
 */
export async function updateUser(userId: number, data: UpdateUserRequest): Promise<UpdateUserResponse> {
  const url = `${API_BASE_URL}/api/admin/users/${userId}`;
  
  // Clean up the data - only include fields that have values (backend supports partial updates)
  // Don't send null or empty strings - just omit the field if it's not being updated
  const cleanedData: UpdateUserRequest = {};
  if (data.email !== undefined && data.email !== null && data.email.trim() !== '') {
    cleanedData.email = data.email.trim();
  }
  if (data.displayName !== undefined && data.displayName !== null && data.displayName.trim() !== '') {
    cleanedData.displayName = data.displayName.trim();
  }
  if (data.company !== undefined) {
    // Send company if it's explicitly null (to clear it) or if it has a value
    cleanedData.company = data.company === null || data.company.trim() === '' ? null : data.company.trim();
  }
  if (data.userType !== undefined) {
    // Send userType if it's explicitly null (to clear it) or if it has a value
    cleanedData.userType = data.userType === null || data.userType === '' ? null : data.userType;
  }
  if (data.roleName !== undefined) {
    // Send roleName if it's explicitly null (to clear it) or if it has a value
    cleanedData.roleName = data.roleName === null || data.roleName.trim() === '' ? null : data.roleName.trim();
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: createAuthHeaders(),
      body: JSON.stringify(cleanedData),
    });
  } catch (networkError) {
    // Handle network errors (connection reset, timeout, etc.)
    console.error('updateUser: Network error:', networkError);
    throw {
      error: true,
      message: `Network error: Unable to connect to backend server. The connection was reset. This may indicate the backend server crashed or rejected the request. Please check the backend logs. Error: ${networkError instanceof Error ? networkError.message : 'Unknown error'}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    console.error('updateUser: Validation error:', error);
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as UserApiError;
  }

  if (response.status === 404) {
    const errorText = await response.text();
    console.error('updateUser: 404 Not Found - URL:', url, 'Response:', errorText);
    // Provide more helpful error message
    throw { 
      error: true, 
      message: `User not found (ID: ${userId}). The backend endpoint may not be implemented yet. Please verify the endpoint exists: ${url}`, 
      code: 'NOT_FOUND' 
    } as UserApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('updateUser: Error response:', response.status, response.statusText, errorText);
    throw {
      error: true,
      message: `Failed to update user: ${response.status} ${response.statusText}. ${errorText ? `Details: ${errorText}` : ''}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  const result = await response.json();

  // Backend returns { success, message, user: { ... with roles: string[] }, roleUpdated }
  // Return the full response for compatibility
  return result as UpdateUserResponse;
}

/**
 * Invite user (Admin only)
 */
export async function inviteUser(data: InviteUserRequest): Promise<InviteUserResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/invite`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      email: data.email.trim(),
      roleName: data.roleName, // Identity role name
      userAccessToken: data.userAccessToken || null, // Include token for delegated auth
    }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 400) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as UserApiError;
  }

  if (!response.ok) {
    // Try to extract error message from response body
    let errorMessage = `Failed to invite user: ${response.statusText}`;
    let errorCode: UserApiError['code'] = 'NETWORK_ERROR';
    
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
      if (errorData.code) {
        errorCode = errorData.code as UserApiError['code'];
      }
    } catch {
      // If response is not JSON, use status text
    }
    
    throw {
      error: true,
      message: errorMessage,
      code: errorCode,
    } as UserApiError;
  }

  return response.json();
}

/**
 * Activate user (Admin only)
 */
export async function activateUser(userId: number): Promise<ActivateUserResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/activate`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'User not found', code: 'NOT_FOUND' } as UserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to activate user: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  return response.json();
}

/**
 * Deactivate user (Admin only)
 */
export async function deactivateUser(userId: number): Promise<DeactivateUserResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/deactivate`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'User not found', code: 'NOT_FOUND' } as UserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to deactivate user: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  return response.json();
}

/**
 * Resend invitation email (Admin only)
 */
export async function resendInvite(userId: number): Promise<{ 
  success: boolean; 
  message: string;
  invitationId?: string;
  email?: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/resend-invite`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as UserApiError;
  }

  if (response.status === 404) {
    const errorText = await response.text();
    throw { 
      error: true, 
      message: errorText || 'User not found or no pending invitations', 
      code: 'NOT_FOUND' 
    } as UserApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Invitation expired or invalid',
      code: 'VALIDATION_ERROR',
    } as UserApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to resend invite: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as UserApiError;
  }

  return response.json();
}

